import { NavLink, useNavigate, Link, useMatch, useLocation } from "react-router-dom";
import { useMemo, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { MfaSettingsDialog } from "@/components/MfaSettingsDialog";
import { SessionActivityDialog } from "@/components/SessionActivityDialog";
import { HelpTrainingDialog } from "@/components/HelpTrainingDialog";
import { LeadRankChip } from "@/components/LeadRankChip";
import { DemoStateBanner } from "@/components/DemoStateBanner";
import { openAuthenticatedInNewTab } from "@/lib/openAuthenticatedInNewTab";
import { useScheduleWarningBadge } from "@/hooks/useScheduleWarningBadge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TIER_LABELS, TIER_COLORS } from "@/lib/constants";
import { LayoutDashboard, CalendarRange, Car, Users, FileSpreadsheet, Flag, LogOut, CalendarCheck, Building2, ShieldCheck, UserCog, Eye, KeyRound, BarChart3, History, Mail, HelpCircle, PanelLeftClose, PanelLeftOpen, Pin, PinOff, Printer, Table2, Activity, Compass, LayoutList } from "lucide-react";

// Feb-2026 RBAC Rework Phase 2 — per-title sidebar visibility.
// Rules:
//   - `editorOnly: true`  — visible iff `user.can_edit === true` (unchanged).
//   - `titles: [...]`     — visible iff `user.title` is in the list; if the
//                           key is absent, defaults to "visible to everyone".
// Fleet Manager + Logistics Lead are hard-scoped to Fleet + Schedule only
// per Phase 2 spec — every other tab hides for them.
const FM_LM_TITLES = ["main_admin", "lead", "ops_manager", "fleet_manager", "logistics_lead", "other", "coach"];
const EDITOR_PLUS_VIEWER = ["main_admin", "lead", "ops_manager", "other"];
// Phase PP-16 (Feb 2026) — Manifests read-only surface. Fleet Manager +
// full editors + viewer see it; Logistics Lead + Coach did not.
// Phase PP-18 (Feb 2026) — Logistics Lead ADDED to the whitelist per
// user directive; both fleet-tier roles now share the same three-page
// scope (Schedule / Fleet / Manifests).
// Phase PP-19 (Feb 2026) — Shop Manager + Shop Member added to
// Manifests + Fleet nav ONLY (they do NOT see Schedule). Both Shop
// roles are hard-scoped to Fleet + Manifests only.
const SHOP_TITLES = ["shop_manager", "shop_member"];
const MANIFESTS_TITLES = ["main_admin", "lead", "ops_manager", "fleet_manager", "logistics_lead", "shop_manager", "shop_member", "other"];
// Feb 2026 — Sidebar visual polish. `groupStart: true` on the FIRST item of a
// logical group triggers a hairline divider above it (except for the very
// first visible item). Today: single split between the operational block
// (Dashboard → Call Times) and the admin/audit tail (Metrics → Access).
// SpeedAssign Core (Feb 2026 · Slice 1F) — Nav consolidated to
// Day | Coaches | Fleet | Manifests | Settings. Secondary surfaces are
// reached via the Settings hub page. Every non-shop title lands on
// /day; shop titles bounce to /fleet inside Protected. Coach-tier no
// longer has a dedicated stub — /my-day now redirects to /day.
const NAV = [
  // Release 5 (Feb 2026) — Supervisor Week Overview. Placed near the
  // top of the sidebar so operations managers can jump to the
  // week-level readiness view without leaving the primary Day flow.
  // The default landing route stays `/day` (Protected + DefaultLandingRedirect).
  { to: "/overview", label: "Overview", icon: LayoutDashboard, testid: "nav-overview", titles: EDITOR_PLUS_VIEWER },
  { to: "/day", label: "Day", icon: CalendarRange, testid: "nav-day", titles: EDITOR_PLUS_VIEWER },
  // Feb 2026 · Program Builder V1 (PB-2). Placed 2nd (Day → Programs
  // → Coaches → Fleet → Manifests → Settings) so the operator's
  // "define structure → run day → manage resources" workflow reads
  // in nav order. See PROGRAM_BUILDER_AUDIT.md §9.
  { to: "/programs", label: "Programs", icon: LayoutList, testid: "nav-programs", titles: EDITOR_PLUS_VIEWER },
  { to: "/coaches", label: "Coaches", icon: Users, testid: "nav-coaches", titles: EDITOR_PLUS_VIEWER },
  { to: "/fleet", label: "Fleet", icon: Car, testid: "nav-fleet", titles: [...FM_LM_TITLES.filter(t => t !== "coach"), ...SHOP_TITLES] },
  { to: "/manifests", label: "Manifests", icon: Printer, testid: "nav-manifests", titles: MANIFESTS_TITLES },
  // Slice 6 (Feb 2026) — Operator Activity Feed. Read-only history
  // over the durable change_log audit source. Gated to editor-plus
  // roles to match the backend `require_editor` on
  // `GET /api/activity/feed`.
  { to: "/activity", label: "Activity", icon: History, testid: "nav-activity", titles: ["main_admin", "lead", "ops_manager"] },
  // Batch 5 (Feb 2026) — Weekly Assignment Board. Read-only replacement
  // for the weekly coach-assignment spreadsheet.
  { to: "/assignment-board", label: "Assignment Board", icon: Table2, testid: "nav-assignment-board", titles: EDITOR_PLUS_VIEWER },
  { to: "/settings", label: "Settings", icon: Compass, testid: "nav-settings", titles: EDITOR_PLUS_VIEWER },
];

// Feb 2026 — collapsible sidebar persistence key. localStorage is pre-approved
// as an intentional preference channel per code_review_ignore.md.
const SIDEBAR_COLLAPSED_KEY = "speedassign.sidebar.collapsed";
// Simplify Batch (Feb 2026, item A2) — sidebar-pin preference. When
// pinned, the auto-collapse-on-scroll on the /schedule route is
// disabled so ops managers who prefer full-width sidebar can opt out
// of the reclamation behavior. Default: unpinned (auto-collapse on).
const SIDEBAR_PINNED_KEY = "speedassign.sidebar.pinned";
// Scroll threshold that triggers auto-collapse when unpinned + on
// the /schedule route.
const SCHEDULE_SCROLL_COLLAPSE_PX = 400;


// Cleanup Sprint (Feb 2026) sidebar sticky-date pill was retired in the
// Feb-2026 Schedule simplify pass (audit item #1 — verbatim duplicate of
// the toolbar's `schedule-date` button). The `useSelectedDate` context
// remains the source of truth for the toolbar and every other consumer;
// only the passive footer chip was deleted.

// Feb 2026 — wraps a nav item in a Radix Tooltip only when the sidebar is
// collapsed. Keeps the expanded-state render path a plain NavLink so nothing
// changes for full-width users.
// Feb 2026 — wraps a nav item in a Radix Tooltip. The Tooltip is ALWAYS
// mounted (avoids re-creating the trigger element when `collapsed` flips,
// which would drop Radix's pointer-event listeners); we suppress the
// tooltip in expanded mode by forcing `open={false}`. In collapsed mode
// `open` is left uncontrolled so Radix handles hover / focus itself.
function CollapsibleNavItem({ item, collapsed }) {
  // Feb 2026 sidebar-polish fix — Radix `TooltipTrigger asChild` uses
  // `<Slot>` which merges the child's `className` via string
  // concatenation, and a NavLink `className={fn}` value is treated as a
  // string in that merge — the function's `toString()` (the raw source
  // code including comments) ends up in the DOM `class` attribute and
  // NONE of the Tailwind utilities are ever applied. Symptoms in the
  // rendered page: labels appear unstyled (default browser layout),
  // stray focus outlines look like border boxes, icons look
  // disconnected from labels. Fix: resolve active state via
  // `useMatch` and pass a pure string className to NavLink — the Slot
  // merge then sees strings on both sides and behaves correctly.
  //
  // Note — `useMatch` with the raw `item.to` handles both `/dashboard`
  // and nested paths like `/coaches/credentials` (each nav item is a
  // literal path, no wildcards). `end: true` prevents `/coaches`
  // matching while on `/coaches/credentials`.
  const match = useMatch({ path: item.to, end: item.to === "/dashboard" });
  const isActive = !!match;

  // Feb 2026 polish — softer, less-loud active state:
  //   * subtle zinc bg (matches hover family, not a jarring red slab)
  //   * 2px Porsche-red left accent bar (kept in the padding gutter
  //     via `pl-[10px] border-l-2 border-[#D5001C]`)
  //   * dedicated ease-out timing for background-color transitions
  // Hover uses `bg-zinc-900/60` for a lighter tint than solid
  // zinc-900 — keeps the list breathing rather than blocky.
  const base = collapsed
    ? "flex min-h-[44px] min-w-[44px] items-center justify-center px-2 py-2.5"
    : "flex items-center gap-2.5 px-3 py-2.5";
  const shared = "rounded-sm text-sm font-medium transition-[background-color,color,border-color] duration-150 ease-out";
  const active = "text-white bg-zinc-900/80 border-l-2 border-[#D5001C]";
  // In expanded mode, the 2px border shifts the label horizontally;
  // compensate via `pl-[10px]` so the icon stays vertically aligned
  // with inactive items.
  const activeSpacing = collapsed ? "" : "pl-[10px]";
  const inactive = "text-zinc-400 hover:text-white hover:bg-zinc-900/60 border-l-2 border-transparent";
  const inactiveSpacing = collapsed ? "" : "pl-[10px]";
  const cls = `${base} ${shared} ${isActive ? `${active} ${activeSpacing}` : `${inactive} ${inactiveSpacing}`}`;

  return (
    <Tooltip open={collapsed ? undefined : false}>
      <TooltipTrigger asChild>
        <NavLink
          to={item.to}
          data-testid={item.testid}
          aria-label={item.label}
          className={cls}
        >
          <span className="relative inline-flex shrink-0">
            <item.icon className={`h-4 w-4 shrink-0 ${item.iconClass || ""}`} />
            {item.badge && (
              <span
                data-testid={`${item.testid}-badge`}
                aria-label={item.badgeLabel || `${item.label} has updates`}
                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#D5001C] ring-1 ring-zinc-950"
              />
            )}
          </span>
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {item.badge && item.badgeLabel ? `${item.label} · ${item.badgeLabel}` : item.label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwOpen, setPwOpen] = useState(false);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [sessOpen, setSessOpen] = useState(false);
  // Batch 4 · #1 (Feb 2026) — Help & Training now opens an in-app
  // dialog instead of trying to fetch the `/docs/ops-manager-manual`
  // PDF (that endpoint's blob response is a live no-op in the
  // preview; endpoint is INTENTIONALLY left in place for a possible
  // later restore). See BATCH4 evidence §1.
  const [helpOpen, setHelpOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    // SSR-safe rehydrate. Default false (expanded) on first visit / when
    // localStorage is unavailable.
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"; }
    catch { return false; }
  });
  // Simplify Batch item A2 (Feb 2026) — pin toggle. Persisted as
  // "true" ⇒ pinned (auto-collapse disabled). Default unpinned so the
  // reclamation behavior is on for the median user; opt-out with one
  // click for ops managers who want the sidebar always visible.
  const [pinned, setPinned] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(SIDEBAR_PINNED_KEY) === "true"; }
    catch { return false; }
  });
  // Scroll-driven auto-collapse state (only active on /schedule while
  // unpinned + not manually collapsed). Kept as a distinct piece of
  // state so the manual `collapsed` preference isn't overwritten when
  // the user scrolls back to the top.
  const [scrollCollapsed, setScrollCollapsed] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "true" : "false"); }
    catch { /* localStorage may be disabled (private mode); ignore. */ }
  }, [collapsed]);
  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_PINNED_KEY, pinned ? "true" : "false"); }
    catch { /* localStorage may be disabled (private mode); ignore. */ }
  }, [pinned]);

  // Simplify Batch item A2 — scroll listener wired only when the user
  // is on the Schedule route AND unpinned. Reclaims ~200px of
  // horizontal real estate as the board scrolls past the fold.
  const onSchedulePath = location.pathname.startsWith("/schedule");
  useEffect(() => {
    if (!onSchedulePath || pinned) {
      setScrollCollapsed(false);
      return;
    }
    const onScroll = () => {
      setScrollCollapsed(window.scrollY > SCHEDULE_SCROLL_COLLAPSE_PX);
    };
    onScroll();  // initialize on mount
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onSchedulePath, pinned]);

  // V2.1a (Feb 2026) — Narrow-viewport responsive collapse. On viewports
  // narrower than the `md` breakpoint (< 768 px, matching Tailwind), the
  // sidebar auto-collapses to its 14-unit (56 px) icon rail so the shared
  // Layout no longer pushes content off-screen. Users can still expand
  // via the same toggle button; on narrow screens that overlays the
  // content column (`fixed` positioning is unchanged). Desktop behavior
  // (>= 768 px) is unchanged — the existing `collapsed` / `scrollCollapsed`
  // preferences take precedence there.
  const [narrowViewport, setNarrowViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.matchMedia("(max-width: 767.98px)").matches; }
    catch { return false; }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(max-width: 767.98px)");
    const onChange = (e) => setNarrowViewport(e.matches);
    setNarrowViewport(mql.matches);
    // Both `addEventListener` (modern) and `addListener` (Safari 13) for compat.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  const toggleCollapsed = useCallback(() => setCollapsed((c) => !c), []);
  const togglePinned = useCallback(() => setPinned((p) => !p), []);
  const effectiveCollapsed = collapsed || scrollCollapsed || narrowViewport;

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  // Sub-Phase 3 (Feb 2026) — memoize the NAV filter so we don't rebuild the
  // visible-nav array on every render (only when user title / can_edit flips).
  // Static 14-entry NAV, so the win is marginal, but it removes the finding
  // from the perf audit and stabilizes the `.map()` output for future
  // React.memo'd NavLink work.
  const visibleNav = useMemo(
    () => {
      // V2.6A · direct activation — Operations nav is always shown for
      // eligible titles; `/schedule` and `/assignments` are always
      // reachable alongside. The previous cutover flag / opsWorkspace
      // rollout gates were retired.
      const _userTitle = user?.title || "other";
      return NAV.filter((item) => {
        if (item.editorOnly && !user?.can_edit) return false;
        if (item.titles && !item.titles.includes(_userTitle)) return false;
        return true;
      });
    },
    [user?.can_edit, user?.title],
  );

  // Package 2 · Item 3 (Feb 2026) — sidebar red-dot on Schedule when
  // the currently-viewed day carries any unacknowledged warning. Only
  // meaningful for users who see the Schedule tab in the first place.
  const canSeeSchedule = visibleNav.some((n) => n.to === "/schedule");
  const { hasUnacked: hasScheduleWarnings, unackedCount: scheduleUnackedCount } =
    useScheduleWarningBadge({ enabled: canSeeSchedule && !!user });

  const decoratedNav = useMemo(
    () => visibleNav.map((item) =>
      item.to === "/schedule" && hasScheduleWarnings
        ? {
            ...item,
            badge: true,
            badgeLabel: `${scheduleUnackedCount} session${scheduleUnackedCount === 1 ? "" : "s"} with unacknowledged warnings`,
          }
        : item,
    ),
    [visibleNav, hasScheduleWarnings, scheduleUnackedCount],
  );

  const asideWidth = effectiveCollapsed ? "w-14" : "w-60";
  const mainOffset = effectiveCollapsed ? "ml-14" : "ml-60";

  return (
    <TooltipProvider delayDuration={200}>
      <div className="min-h-screen bg-background flex" data-testid="app-layout-root" data-sidebar-collapsed={effectiveCollapsed ? "true" : "false"} data-sidebar-pinned={pinned ? "true" : "false"}>
        <aside
          data-testid="app-sidebar"
          className={`${asideWidth} shrink-0 border-r border-zinc-900 bg-[#0c0c0c] flex flex-col fixed h-screen transition-[width] duration-150 ease-out overflow-hidden`}
        >
          <Link to="/day" data-testid="logo-home-link" title="Back to Day workspace"
            className={`h-16 flex items-center ${effectiveCollapsed ? "justify-center px-0" : "gap-2 px-5"} border-b border-zinc-900 hover:bg-zinc-900/40 transition-colors`}>
            <div className="h-8 w-8 grid place-items-center bg-[#D5001C] rounded-sm shrink-0">
              <Flag className="h-4 w-4 text-white" />
            </div>
            {!effectiveCollapsed && (
              <div>
                <div className="font-head font-bold tracking-tight text-sm leading-none">PEC ATL</div>
                <div className="label-tag text-[0.55rem]">Operations</div>
              </div>
            )}
          </Link>

          {/* Feb 2026 — Sidebar collapse toggle. Sits above the nav list so it's
              always reachable via keyboard and doesn't compete with content.
              Simplify Batch item A2 — pin button rendered alongside so ops
              managers on /schedule can opt out of auto-collapse-on-scroll. */}
          <div className={`${effectiveCollapsed ? "flex justify-center gap-1" : "flex justify-end gap-1"} px-2 pt-2`}>
            {onSchedulePath && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={togglePinned}
                    data-testid="sidebar-pin-toggle"
                    aria-label={pinned ? "Unpin sidebar (allow auto-collapse on scroll)" : "Pin sidebar (disable auto-collapse on scroll)"}
                    aria-pressed={pinned}
                    className={`inline-flex min-w-[44px] items-center justify-center h-11 w-11 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 transition-colors ${pinned ? "text-[#D5001C] hover:text-white hover:bg-zinc-900" : "text-zinc-500 hover:text-white hover:bg-zinc-900"}`}
                  >
                    {pinned ? <Pin className="h-4 w-4" /> : <PinOff className="h-4 w-4" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  {pinned ? "Sidebar pinned open (unpin to auto-collapse on scroll)" : "Auto-collapses on Schedule scroll (click to pin open)"}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  data-testid="sidebar-collapse-toggle"
                  aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                  aria-expanded={!collapsed}
                  className="inline-flex min-w-[44px] items-center justify-center h-11 w-11 rounded-sm text-zinc-500 hover:text-white hover:bg-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-500 transition-colors"
                >
                  {effectiveCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {collapsed ? "Expand sidebar" : "Collapse sidebar"}
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Ops — Cleanup Pass Phase 3 (Feb 2026) — passive env-state indicator.
              Hidden when collapsed (needs horizontal room for the label). */}
          {!effectiveCollapsed && (
            <div className="px-3 pt-2.5" data-testid="demo-state-banner-slot">
              <DemoStateBanner />
            </div>
          )}

          <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
            {decoratedNav.map((item, i) => (
              <div key={item.to}>
                {item.groupStart && i > 0 && (
                  <div
                    data-testid={`nav-group-separator-${item.testid}`}
                    aria-hidden="true"
                    className={`${effectiveCollapsed ? "mx-2" : "mx-3"} my-2 border-t border-zinc-900/70`}
                  />
                )}
                <CollapsibleNavItem item={item} collapsed={effectiveCollapsed} />
              </div>
            ))}
          </nav>

          <div className={`${effectiveCollapsed ? "px-2" : "p-3"} py-3 border-t border-zinc-900 space-y-1`}>
            {/* Simplify Batch item #1 (Feb 2026) — Sidebar date pill retired.
                The toolbar's `schedule-date` button owns the current-date
                display; the sidebar pill was a verbatim duplicate. */}
            {/* Feb 2027 — Help link (universal; visible to all authenticated roles).
                Opens the Ops Manager Training Manual PDF in a new tab. Auth-gated
                at the API layer via `require_read_or_higher`.
                Feb 2026 (S-2 401 regression fix) — Rendered as a <button> that
                fires an authenticated blob-fetch via `api` and opens the
                result in a new tab. Plain `<a href>` no longer works because
                the access-token cookie was narrowed to `/api/auth` and an
                anchor cannot attach a Bearer header. */}
            <Tooltip open={effectiveCollapsed ? undefined : false}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  data-testid="help-training-link"
                  aria-label="Help & Training"
                  aria-haspopup="dialog"
                  className={`min-h-[44px] w-full flex items-center ${effectiveCollapsed ? "min-w-[44px] justify-center px-2 mb-1" : "gap-2.5 px-3 mb-2"} py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition-[background-color,color] duration-150 ease-out`}
                >
                  <HelpCircle className="h-4 w-4" />
                  {!effectiveCollapsed && <span>Help &amp; Training</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>Help &amp; Training</TooltipContent>
            </Tooltip>
            {!effectiveCollapsed && (
              <div className="px-2 mb-2" data-testid="user-badge">
                {/*
                  Slice 7 · Global names-only privacy (Feb 2026).
                  The signed-in email is intentionally NOT rendered
                  here — display name + plain-language role only.
                  Regression is locked by
                  `components/Layout.userBadgeNoEmail.test.jsx`.
                */}
                <div className="font-semibold text-sm truncate" data-testid="user-badge-name">{user?.name || "Admin"}</div>
                {user && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {(() => {
                      const label = {
                        main_admin: "Main Admin",
                        ops_manager: "Ops Manager",
                        lead: "Lead",
                        fleet_manager: "Fleet Manager",
                        logistics_lead: "Logistics",
                        shop_manager: "Shop Manager",
                        shop_member: "Shop Member",
                        coach: "Coach",
                        other: "Read-only",
                      }[user.title || "other"] || "Read-only";
                      const editor = user.can_edit;
                      return (
                        <span data-testid="role-badge"
                          className={`inline-flex items-center gap-1 label-tag px-1.5 py-0.5 rounded-sm border ${editor ? "text-emerald-400 border-emerald-800/70 bg-emerald-950/30" : "text-amber-400 border-amber-800/70 bg-amber-950/20"}`}>
                          {editor ? <ShieldCheck className="h-2.5 w-2.5" /> : <Eye className="h-2.5 w-2.5" />}{label}
                        </span>
                      );
                    })()}
                    {user.tier && TIER_LABELS[user.tier] && (
                      <span data-testid="user-badge-tier"
                        className={`inline-flex items-center label-tag px-1.5 py-0.5 rounded-sm border ${TIER_COLORS[user.tier] || ""}`}>
                        {TIER_LABELS[user.tier]}
                      </span>
                    )}
                    {user.lead_rank && (
                      <LeadRankChip rank={user.lead_rank} size="sm" id="user-badge" />
                    )}
                  </div>
                )}
              </div>
            )}
            {effectiveCollapsed ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setPwOpen(true)}
                      data-testid="change-password-btn"
                      aria-label="Change password"
                      className="min-h-[44px] min-w-[44px] w-full flex items-center justify-center px-2 py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition-[background-color,color] duration-150 ease-out"
                    >
                      <KeyRound className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>Change password</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setMfaOpen(true)}
                      data-testid="mfa-settings-btn"
                      aria-label="Two-factor authentication"
                      className="min-h-[44px] min-w-[44px] w-full flex items-center justify-center px-2 py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition-[background-color,color] duration-150 ease-out"
                    >
                      <ShieldCheck className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>Two-factor auth</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setSessOpen(true)}
                      data-testid="session-activity-btn"
                      aria-label="Session activity"
                      className="min-h-[44px] min-w-[44px] w-full flex items-center justify-center px-2 py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition-[background-color,color] duration-150 ease-out"
                    >
                      <Activity className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>Session activity</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleLogout}
                      data-testid="logout-btn"
                      aria-label="Sign out"
                      className="min-h-[44px] min-w-[44px] w-full flex items-center justify-center px-2 py-2 rounded-sm text-sm font-medium text-zinc-400 hover:text-white hover:bg-zinc-900/60 transition-[background-color,color] duration-150 ease-out"
                    >
                      <LogOut className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>Sign out</TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                <Button onClick={() => setPwOpen(true)} data-testid="change-password-btn" variant="ghost"
                  className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm">
                  <KeyRound className="h-4 w-4" /> Change password
                </Button>
                <Button onClick={() => setMfaOpen(true)} data-testid="mfa-settings-btn" variant="ghost"
                  className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm">
                  <ShieldCheck className="h-4 w-4" /> Two-factor auth
                </Button>
                <Button onClick={() => setSessOpen(true)} data-testid="session-activity-btn" variant="ghost"
                  className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm">
                  <Activity className="h-4 w-4" /> Session activity
                </Button>
                <Button onClick={handleLogout} data-testid="logout-btn" variant="ghost"
                  className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm">
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>
              </>
            )}
          </div>
        </aside>
        <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
        <MfaSettingsDialog open={mfaOpen} onOpenChange={setMfaOpen} />
        <SessionActivityDialog open={sessOpen} onOpenChange={setSessOpen} />
        <HelpTrainingDialog open={helpOpen} onOpenChange={setHelpOpen} />

        <main className={`flex-1 min-w-0 ${mainOffset} min-h-screen transition-[margin] duration-150 ease-out`}>
          <div className="w-full max-w-screen-2xl mx-auto px-3 sm:px-6 md:px-8 py-6 md:py-8 min-w-0 overflow-x-hidden">{children}</div>
        </main>
      </div>
    </TooltipProvider>
  );
}
